import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { MaiaUploadService } from "../../data-access-layer/maia/maia-upload.service";
import { SSE_ENABLED, SseEvent, SseStreamError } from "../../core/sse";
import {
    SovJobEvent,
    SovUploadResponse,
    SovUploadSheet,
    SovUploadStatus,
} from "../../model/maia/sov-upload";

/**
 * MAIA wizard — Upload.
 *
 * Design reference:
 *   docs/AI/Screenshot from 2026-08-07 23-22-30.png — "Upload your data file"
 *
 * Owns only the file/parse lifecycle. Review geocoding is a separate sibling
 * step (MaiaReviewStepComponent, see maia-page) — this component just emits
 * `uploaded` once parsing finishes and the page shell switches to it.
 *
 * Flow: pick or drop exactly one file -> validate client-side -> POST to
 * /sov/dataset/{datasetId}/upload. On success `uploaded` fires; on failure
 * the user stays here with the backend's message.
 */
@Component({
    selector: "maia-upload-step",
    templateUrl: "./maia-upload-step.component.html",
    styleUrls: ["./maia-upload-step.component.scss"],
})
export class MaiaUploadStepComponent implements OnDestroy {
    /** Dataset the SOV file belongs to — taken from the route by the page shell. */
    @Input() datasetId: string | null = null;

    /**
     * Raised by the card's close (×) control. The payload says whether unsaved
     * progress would be lost, so the shell can confirm before closing.
     */
    @Output() readonly closeRequested = new EventEmitter<boolean>();

    /**
     * Raised when the job came back COMPLETE — the upload only updated records
     * that already exist, so there is no wizard to run. The shell returns to
     * the map rather than opening the first step.
     */
    @Output() readonly alreadyComplete = new EventEmitter<SovUploadResponse>();

    /** Raised with the backend response once a file parses successfully. */
    @Output() readonly uploaded = new EventEmitter<SovUploadResponse>();

    /**
     * JIRA-SOV-013: "Resume job #N" — the upload was refused because the user already has an active
     * job on this dataset (409 SOV_OWN_ACTIVE_JOB_EXISTS); the page resumes that job instead.
     */
    @Output() readonly resumeRequested = new EventEmitter<number>();

    /** Raised when Submit is clicked on the review screen - advances to the next wizard step. */
    @Output() readonly submitted = new EventEmitter<void>();

    @ViewChild("fileInput") private readonly fileInput?: ElementRef<HTMLInputElement>;

    /** Drives the drag-over styling on the drop zone. */
    isDragOver = false;

    /** True while the upload request is in flight. */
    isUploading = false;

    /**
     * True when the backend answered PARSING — the file landed but parsing is
     * still running. Resolving this needs a job-status endpoint to poll; until
     * that exists the user is told parsing is under way.
     */
    isParsing = false;

    /** Last successful response, drives the review screen. */
    uploadResponse: SovUploadResponse | null = null;

    /** Whether the Upload Guidelines panel is open. */
    showGuidelines = false;

    /** Live stage text pushed over SSE while parsing. */
    parsingMessage: string | null = null;

    /** 0–100 when the stream reports progress, otherwise null. */
    parsingPercent: number | null = null;

    /**
     * Row count from the job stream. The terminal frame carries `totalRows`
     * rather than a `summary` object, so this backs the review screen when no
     * summary was returned.
     */
    jobTotalRows: number | null = null;

    private jobEvents?: Subscription;

    /**
     * Status from the last frame seen on the stream.
     *
     * The observable's `complete` callback carries no payload, so the terminal
     * status has to be captured as the frame goes past in order to tell
     * PARSED (run the wizard) from COMPLETE (skip it).
     */
    private lastStreamStatus: SovUploadStatus | null = null;
    private templateDownload?: Subscription;

    /** True while the SOV template is being fetched. */
    isDownloadingTemplate = false;
    /** Set when the template download failed. */
    templateError = "";

    /** The file the user picked. */
    selectedFile: File | null = null;

    /** JIRA-SOV-013: the user's already-active job on this dataset, when an upload was refused because of it; null otherwise. */
    activeJobId: number | null = null;

    /** Client-side validation or backend error, shown on the upload view. */
    errorMessage: string | null = null;

    /**
     * The screen says "CSV, XLSX, JSON"; the developer handoff says
     * ".csv, .xlsx, .xls". Both are accepted so neither source is contradicted.
     */
    readonly acceptedExtensions = [".csv", ".xlsx", ".xls", ".json"];

    readonly maxFileSizeBytes = 50 * 1024 * 1024;

    constructor(private readonly maiaUploadService: MaiaUploadService) {}

    ngOnDestroy(): void {
        this.stopJobStream();
        if (this.templateDownload) {
            this.templateDownload.unsubscribe();
        }
    }

    /** Upload in flight, or accepted and still parsing server-side. */
    get isBusy(): boolean {
        return this.isUploading || this.isParsing;
    }

    /** True once a file has been taken on — closing now would discard it. */
    get hasUnsavedProgress(): boolean {
        return !!this.selectedFile || !!this.uploadResponse;
    }

    openGuidelines(): void {
        this.showGuidelines = true;
    }

    closeGuidelines(): void {
        this.showGuidelines = false;
    }

    /**
     * "Download Template" in the guidelines panel — fetches the blank SOV
     * workbook and hands it to the browser.
     */
    downloadTemplate(): void {
        if (this.isDownloadingTemplate) {
            return;
        }

        this.isDownloadingTemplate = true;
        this.templateError = "";

        this.templateDownload = this.maiaUploadService.downloadTemplate().subscribe(
            (response) => {
                this.isDownloadingTemplate = false;
                if (!response.body) {
                    this.templateError = "The template came back empty. Please try again.";
                    return;
                }
                this.saveBlob(response.body, this.templateFileName(response));
            },
            () => {
                this.isDownloadingTemplate = false;
                this.templateError = "We couldn't download the template. Please try again.";
            }
        );
    }

    /**
     * Filename from Content-Disposition, falling back to a sensible default.
     *
     * The header is only readable cross-origin when the server sends
     * `Access-Control-Expose-Headers: Content-Disposition`, so the fallback is
     * the common case rather than an edge case.
     */
    private templateFileName(response: HttpResponse<Blob>): string {
        const disposition = response.headers.get("Content-Disposition") || "";

        const encoded = disposition.match(/filename\*=(?:UTF-8'')?"?([^";]+)"?/i);
        if (encoded && encoded[1]) {
            try {
                return decodeURIComponent(encoded[1]);
            } catch {
                return encoded[1];
            }
        }

        const plain = disposition.match(/filename="?([^";]+)"?/i);
        if (plain && plain[1]) {
            return plain[1].trim();
        }

        return "sov-template.xlsx";
    }

    private saveBlob(blob: Blob, name: string): void {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    requestClose(): void {
        this.closeRequested.emit(this.hasUnsavedProgress);
    }

    get acceptAttribute(): string {
        return this.acceptedExtensions.join(",");
    }

    /**
     * Total parsed records, from whichever source actually has a number yet.
     *
     * Right after upload, `summary` can already be attached but still zeroed
     * (VAPT/geocode counters start at 0 and fill in as those stages run —
     * see SovUploadSummaryRepository.create/incrementCounters on the backend),
     * so a present-but-zero summary is treated the same as no summary: fall
     * back to `jobTotalRows` (the job stream's real row count), and only if
     * that isn't available either, the per-sheet `estimatedRowCount` sum the
     * "Sheets detected" list shows — the least reliable source, since for a
     * CSV upload the backend can't cheaply pre-count rows and sends -1 (its
     * "not yet known" sentinel) per sheet instead of a real estimate; XLSX
     * carries real per-sheet counts from its structured metadata, which is
     * why this bug was CSV-only.
     */
    get totalRecords(): number | null {
        const summary = this.uploadResponse ? this.uploadResponse.summary : null;
        if (summary && summary.totalRecords > 0) {
            return summary.totalRecords;
        }
        if (this.jobTotalRows !== null) {
            return this.jobTotalRows;
        }
        const estimated = this.estimatedRecordsFromSheets;
        if (estimated !== null) {
            return estimated;
        }
        return summary ? summary.totalRecords : null;
    }

    /** Sum of included sheets' `estimatedRowCount` — null when there's nothing (reliable) to sum yet. */
    private get estimatedRecordsFromSheets(): number | null {
        if (!this.sheets.length) {
            return null;
        }
        const included = this.sheets.filter((sheet) => sheet.included);
        // -1 is the backend's "not yet known" sentinel (SovConstants.UNKNOWN_ROW_COUNT), not a real
        // count — summing it in would silently produce a negative total (seen on CSV uploads).
        if (included.some((sheet) => sheet.estimatedRowCount < 0)) {
            return null;
        }
        return included.reduce((total, sheet) => total + sheet.estimatedRowCount, 0);
    }

    /** Column count of the included MAPTYCS sheet — the only sheet whose data is parsed. */
    private get includedColumnCount(): number | null {
        const included = this.sheets.find((sheet) => sheet.included && sheet.columnCount > 0);
        return included ? included.columnCount : null;
    }

    /** File name, size, and — once parsed — the row and column counts. */
    get fileSummary(): string {
        if (!this.selectedFile) {
            return "";
        }

        const parts = [
            this.selectedFile.name,
            this.formatFileSize(this.selectedFile.size),
        ];

        const records = this.totalRecords;
        if (records !== null) {
            parts.push(`${records.toLocaleString()} rows`);
        }
        const columns = this.includedColumnCount;
        if (columns !== null) {
            parts.push(`${columns} columns`);
        }

        return parts.join(" · ");
    }

    /** Backs `fileSummary`/`totalRecords` only — the review screen gets its own copy via `@Input`. */
    private get sheets(): SovUploadSheet[] {
        return this.uploadResponse && this.uploadResponse.sheets
            ? this.uploadResponse.sheets
            : [];
    }

    openFilePicker(): void {
        if (this.isBusy) {
            return;
        }
        this.errorMessage = null;
        if (this.fileInput) {
            this.fileInput.nativeElement.click();
        }
    }

    onFileInputChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.handleFileList(input.files);
        // Allows re-picking the same file straight after an error.
        input.value = "";
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        if (!this.isBusy) {
            this.isDragOver = true;
        }
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
        if (this.isBusy) {
            return;
        }
        this.handleFileList(event.dataTransfer ? event.dataTransfer.files : null);
    }

    /** Clears the current selection and returns to the drop zone. */
    replaceFile(): void {
        this.stopJobStream();
        this.selectedFile = null;
        this.errorMessage = null;
        this.uploadResponse = null;
        this.lastStreamStatus = null;
        this.isParsing = false;
        this.parsingMessage = null;
        this.parsingPercent = null;
        this.jobTotalRows = null;
    }

    /** JIRA-SOV-013: the conflicting job's id from a 409 SOV_OWN_ACTIVE_JOB_EXISTS body, or null for any other error. */
    private activeJobIdOf(error: any): number | null {
        const body = error && error.status === 409 ? error.error : null;
        return body && body.code === "SOV_OWN_ACTIVE_JOB_EXISTS" && typeof body.activeJobId === "number"
            ? body.activeJobId
            : null;
    }

    /** JIRA-SOV-013: "Resume job #N" — hand the active job to the page instead of uploading. */
    resumeActiveJob(): void {
        if (this.activeJobId !== null) {
            this.resumeRequested.emit(this.activeJobId);
        }
    }

    /** JIRA-SOV-013: "Close it and upload this file" — close the active job, then re-send the same file. */
    closeActiveJobAndUpload(): void {
        if (this.activeJobId === null || this.isBusy) {
            return;
        }
        this.isUploading = true;
        this.errorMessage = null;
        this.maiaUploadService.closeJob(this.activeJobId).subscribe({
            next: () => {
                this.isUploading = false;
                this.activeJobId = null;
                this.retryUpload();
            },
            error: (error) => {
                this.isUploading = false;
                this.errorMessage = this.extractErrorMessage(error);
            },
        });
    }

    /** Re-sends the file that failed, without making the user pick it again. */
    retryUpload(): void {
        if (this.selectedFile && !this.isBusy) {
            this.upload(this.selectedFile);
        }
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) {
            return `${bytes} B`;
        }
        const kb = bytes / 1024;
        if (kb < 1024) {
            return `${kb.toFixed(1)} KB`;
        }
        return `${(kb / 1024).toFixed(1)} MB`;
    }

    private handleFileList(files: FileList | null): void {
        if (!files || !files.length) {
            return;
        }

        if (files.length > 1) {
            this.selectedFile = null;
            this.errorMessage = "Please choose one file at a time.";
            return;
        }

        const file = files[0];
        const validationError = this.validate(file);
        if (validationError) {
            this.selectedFile = null;
            this.errorMessage = validationError;
            return;
        }

        this.selectedFile = file;
        this.errorMessage = null;
        this.upload(file);
    }

    private upload(file: File): void {
        if (!this.datasetId) {
            this.errorMessage = "Missing dataset. Please reopen MAIA from the map.";
            return;
        }

        this.stopJobStream();
        this.isUploading = true;
        this.isParsing = false;
        this.parsingMessage = null;
        this.parsingPercent = null;
        this.jobTotalRows = null;
        this.errorMessage = null;
        this.activeJobId = null;
        this.uploadResponse = null;

        this.maiaUploadService.uploadSovFile(this.datasetId, file).subscribe(
            (response) => {
                this.isUploading = false;
                this.handleUploadResponse(response);
            },
            (error) => {
                this.isUploading = false;
                this.isParsing = false;
                this.errorMessage = this.extractErrorMessage(error);
                this.activeJobId = this.activeJobIdOf(error);
            }
        );
    }

    private handleUploadResponse(response: SovUploadResponse): void {
        this.uploadResponse = response;

        switch (response ? response.status : null) {
            case SovUploadStatus.PARSED:
                this.isParsing = false;
                this.uploaded.emit(response);
                return;

            case SovUploadStatus.COMPLETE:
                // Update-only upload: nothing to geocode or enrich, so the
                // wizard is skipped entirely.
                this.isParsing = false;
                this.alreadyComplete.emit(response);
                return;

            case SovUploadStatus.PARSING:
                // Accepted, still parsing. Follow the job over SSE; if that
                // stream is unavailable the spinner simply stays put, which is
                // the behaviour we had before.
                this.isParsing = true;
                this.startJobStream(response.jobId);
                return;

            case SovUploadStatus.FAILED:
                this.isParsing = false;
                this.errorMessage =
                    (response && (response.message || response.error)) ||
                    "We couldn't parse that file. Please check its contents and try again.";
                return;

            default:
                this.isParsing = false;
                this.errorMessage = "Unexpected response from the server. Please try again.";
        }
    }

    /**
     * Follows a parsing job over SSE.
     *
     * POC: if the stream cannot be opened, the component keeps the "parsing"
     * spinner exactly as it behaved before — no error is surfaced, because a
     * missing stream is not a failed upload.
     */
    private startJobStream(jobId: number): void {
        this.stopJobStream();

        if (!SSE_ENABLED) {
            return;
        }

        if (jobId === undefined || jobId === null) {
            return;
        }

        this.jobEvents = this.maiaUploadService.streamJobEvents(jobId).subscribe({
            next: (event) => this.applyProgress(event),
            error: (error) => this.onStreamError(error),
            complete: () => this.onParsed(),
        });
    }

    private stopJobStream(): void {
        if (this.jobEvents) {
            this.jobEvents.unsubscribe();
            this.jobEvents = undefined;
        }
    }

    /** Progress frame — update the live labels, nothing terminal. */
    private applyProgress(event: SseEvent<SovJobEvent>): void {
        const payload = event.data;
        if (!payload) {
            // Non-JSON frame: show it as the stage label rather than dropping it.
            if (event.raw) {
                this.parsingMessage = event.raw;
            }
            return;
        }

        if (payload.status) {
            this.lastStreamStatus = payload.status;
        }

        if (typeof payload.percent === "number") {
            this.parsingPercent = Math.max(0, Math.min(100, Math.round(payload.percent)));
        }
        if (typeof payload.totalRows === "number") {
            this.jobTotalRows = payload.totalRows;
        }
        if (payload.message) {
            this.parsingMessage = payload.message;
        }
        if (payload.sheets) {
            this.mergeIntoResponse({ sheets: payload.sheets });
        }
    }

    /**
     * The stream ended. PARSED moves on to the review screen; COMPLETE means
     * the job was an update of existing records and needs no wizard at all.
     */
    private onParsed(): void {
        const wasComplete = this.lastStreamStatus === SovUploadStatus.COMPLETE;

        this.stopJobStream();
        this.isParsing = false;
        this.parsingMessage = null;
        this.parsingPercent = null;
        this.mergeIntoResponse({
            status: wasComplete ? SovUploadStatus.COMPLETE : SovUploadStatus.PARSED,
        });

        if (!this.uploadResponse) {
            return;
        }

        if (wasComplete) {
            this.alreadyComplete.emit(this.uploadResponse);
            return;
        }
        this.uploaded.emit(this.uploadResponse);
    }

    /**
     * The stream ended badly. A FAILED frame is a real parse failure and is
     * shown; a transport error just means we lost the live channel, which is
     * not the upload's fault — the spinner stays.
     */
    private onStreamError(error: any): void {
        this.stopJobStream();

        if (!(error instanceof SseStreamError)) {
            return;
        }

        const payload = (error.event as SseEvent<SovJobEvent>).data;
        this.isParsing = false;
        this.parsingMessage = null;
        this.parsingPercent = null;
        this.errorMessage =
            (payload && (payload.error || payload.message)) ||
            "We couldn't parse that file. Please check its contents and try again.";
    }

    /** Folds streamed fields onto the response backing the review screen. */
    private mergeIntoResponse(patch: Partial<SovUploadResponse>): void {
        this.uploadResponse = {
            ...(this.uploadResponse as SovUploadResponse),
            ...patch,
        };
    }

    private extractErrorMessage(error: any): string {
        const fallback = "Upload failed. Please try again.";

        if (!error) {
            return fallback;
        }

        if (error instanceof HttpErrorResponse) {
            if (error.status === 0) {
                return "Could not reach the server. Check your connection and try again.";
            }
            const body = error.error;
            if (typeof body === "string" && body.trim()) {
                return body;
            }
            if (body && typeof body.message === "string" && body.message.trim()) {
                return body.message;
            }
            if (body && typeof body.error === "string" && body.error.trim()) {
                return body.error;
            }
            return error.message || fallback;
        }

        if (typeof error === "string" && error.trim()) {
            return error;
        }

        return error.message || fallback;
    }

    private validate(file: File): string | null {
        const name = file.name.toLowerCase();
        const hasAllowedExtension = this.acceptedExtensions.some((extension) =>
            name.endsWith(extension)
        );

        if (!hasAllowedExtension) {
            return "Unsupported file type. Please choose a CSV, XLSX, XLS or JSON file.";
        }

        if (file.size > this.maxFileSizeBytes) {
            return `File is too large (${this.formatFileSize(file.size)}). The limit is 50MB.`;
        }

        if (file.size === 0) {
            return "That file is empty. Please choose a file with data in it.";
        }

        return null;
    }
}
